import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import Modal from '../ui/Modal';
import ringImg from '../../assets/ring.png';
import mainEventerTitleImg from '../../assets/me_title.png';
import {
  buildFactionShareCardData,
  getFactionShareText,
} from '../../utils/factionShare';
import { getCorsSafeImageUrl } from '../../utils/remoteImageProxy';
import './FactionShareButton.css';

const CAPTURE_WIDTH = 1080;
const CAPTURE_HEIGHT = 1920;
const PROGRESS_RING_RADIUS = 52;
const PROGRESS_RING_CIRCUMFERENCE = 2 * Math.PI * PROGRESS_RING_RADIUS;
const IMAGE_RETRY_ATTEMPTS = 3;
const IMAGE_RETRY_DELAY_MS = 350;

const SOCIAL_PLATFORMS = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: 'bi-whatsapp',
    fallbackMessage:
      'Immagine scaricata. Se il device non supporta il file diretto, si apre WhatsApp con il link della fazione.',
    buildUrl: ({ url, text }) =>
      `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    id: 'telegram',
    label: 'Telegram',
    icon: 'bi-telegram',
    fallbackMessage:
      'Immagine scaricata. Se il device non supporta il file diretto, si apre Telegram con il link della fazione.',
    buildUrl: ({ url, text }) =>
      `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    icon: 'bi-facebook',
    fallbackMessage:
      'Immagine scaricata. Se il device non supporta il file diretto, si apre Facebook con il link della fazione.',
    buildUrl: ({ url }) =>
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: 'instagram',
    label: 'Instagram',
    icon: 'bi-instagram',
    fallbackMessage:
      'Immagine scaricata. Apri Instagram e scegli la card dalla galleria per pubblicarla o inviarla.',
    buildUrl: null,
  },
];

const sanitizeFileName = (value = '') =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'fazione';

const wait = (duration) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, duration);
  });

const appendRetryToken = (assetUrl, attempt) => {
  if (!assetUrl) return assetUrl;

  try {
    const parsedUrl = new URL(
      assetUrl,
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
    );
    parsedUrl.searchParams.set('shareRetry', `${Date.now()}-${attempt}`);
    return parsedUrl.toString();
  } catch (error) {
    return assetUrl;
  }
};

const preloadImageAsset = (assetUrl, { crossOrigin = 'anonymous' } = {}) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const shouldSetCrossOrigin =
      Boolean(crossOrigin) &&
      typeof assetUrl === 'string' &&
      !assetUrl.startsWith('data:') &&
      !assetUrl.startsWith('blob:');

    if (shouldSetCrossOrigin) {
      image.crossOrigin = crossOrigin;
    }

    image.decoding = 'async';

    image.onload = () => {
      if (image.naturalWidth > 0 && image.naturalHeight > 0) {
        resolve(assetUrl);
        return;
      }

      reject(new Error('EMPTY_IMAGE'));
    };

    image.onerror = () => {
      reject(new Error('LOAD_FAILED'));
    };

    image.src = assetUrl;
  });

const preloadRequiredImageWithRetry = async (assetUrl, label) => {
  if (!assetUrl) {
    throw new Error(`Immagine wrestler mancante: ${label}`);
  }

  for (let attempt = 0; attempt < IMAGE_RETRY_ATTEMPTS; attempt += 1) {
    const attemptUrl = attempt === 0 ? assetUrl : appendRetryToken(assetUrl, attempt);

    try {
      await preloadImageAsset(attemptUrl);
      return;
    } catch (error) {
      if (attempt === IMAGE_RETRY_ATTEMPTS - 1) {
        throw new Error(`Errore caricamento immagine wrestler: ${label}`);
      }

      await wait(IMAGE_RETRY_DELAY_MS);
    }
  }
};

const ensureRequiredMemberImagesReady = async (sections, onProgress = null) => {
  const uniqueEntries = Array.from(
    new Map(
      sections
        .flatMap((section) =>
          section.members.map((member) => [
            `${member.id || member.name}-${member.imageUrl || 'missing'}`,
            {
              label: member.name || 'Wrestler sconosciuto',
              src: getCorsSafeImageUrl(member.imageUrl) || '',
            },
          ])
        )
        .filter(Boolean)
    ).values()
  );

  const totalImages = uniqueEntries.length;
  let loadedImages = 0;

  if (totalImages === 0) {
    onProgress?.(1);
    return;
  }

  await Promise.all(
    uniqueEntries.map(async (entry) => {
      await preloadRequiredImageWithRetry(entry.src, entry.label);
      loadedImages += 1;
      onProgress?.(Math.min(loadedImages / totalImages, 1));
    })
  );
};

const waitForCaptureAssets = async (root, onProgress = null) => {
  if (!root) return;

  const images = Array.from(root.querySelectorAll('img'));
  const totalImages = images.length;
  let loadedImages = 0;

  const reportProgress = () => {
    if (!onProgress) return;

    if (totalImages === 0) {
      onProgress(1);
      return;
    }

    onProgress(Math.min(loadedImages / totalImages, 1));
  };

  await Promise.all(
    images.map((image) => {
      if (image.complete) {
        loadedImages += 1;
        reportProgress();
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        const handleDone = () => {
          loadedImages += 1;
          reportProgress();
          image.removeEventListener('load', handleDone);
          image.removeEventListener('error', handleDone);
          resolve();
        };

        image.addEventListener('load', handleDone);
        image.addEventListener('error', handleDone);
      });
    })
  );

  if (document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch (error) {
      console.warn('Font non pronti per la card share:', error);
    }
  }

  onProgress?.(1);
};

const canvasToFile = async (canvas, fileName) => {
  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png', 1);
  });

  if (!blob) {
    throw new Error('Impossibile generare il file immagine');
  }

  return new File([blob], fileName, { type: 'image/png' });
};

const getGridMetrics = (count, zone = 'ring') => {
  if (zone === 'main') {
    return {
      '--share-min-column': '420px',
      '--share-card-height': '500px',
      '--share-grid-gap': '16px',
    };
  }

  if (zone === 'all') {
    if (count >= 18) {
      return {
        '--share-min-column': '122px',
        '--share-card-height': '156px',
        '--share-grid-gap': '8px',
      };
    }

    if (count >= 13) {
      return {
        '--share-min-column': '138px',
        '--share-card-height': '172px',
        '--share-grid-gap': '10px',
      };
    }

    return {
      '--share-min-column': '166px',
      '--share-card-height': '232px',
      '--share-grid-gap': '12px',
    };
  }

  if (zone === 'backstage') {
    if (count >= 10) {
      return {
        '--share-min-column': '116px',
        '--share-card-height': '142px',
        '--share-grid-gap': '8px',
      };
    }

    if (count >= 6) {
      return {
        '--share-min-column': '132px',
        '--share-card-height': '162px',
        '--share-grid-gap': '10px',
      };
    }

    return {
      '--share-min-column': '154px',
      '--share-card-height': '208px',
      '--share-grid-gap': '12px',
    };
  }

  if (count >= 12) {
    return {
      '--share-min-column': '124px',
      '--share-card-height': '160px',
      '--share-grid-gap': '8px',
    };
  }

  if (count >= 8) {
    return {
      '--share-min-column': '140px',
      '--share-card-height': '184px',
      '--share-grid-gap': '10px',
    };
  }

  return {
    '--share-min-column': '168px',
    '--share-card-height': '236px',
    '--share-grid-gap': '12px',
  };
};

function ShareMemberCard({ member, variant = 'ring' }) {
  if (!member) return null;

  return (
    <article
      className={[
        'faction-share-member',
        `faction-share-member--${variant}`,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <img
        src={getCorsSafeImageUrl(member.imageUrl)}
        alt={member.name}
        className="faction-share-member__image"
        crossOrigin="anonymous"
      />
      <div className="faction-share-member__gradient" />
      {variant === 'main' && (
        <img
          src={mainEventerTitleImg}
          alt=""
          aria-hidden="true"
          className="faction-share-member__belt"
        />
      )}
      <div className="faction-share-member__name">
        <span>{member.name}</span>
      </div>
    </article>
  );
}

export default function FactionShareButton({
  faction,
  members = [],
  roster = null,
  buttonClassName = 'btn btn-outline-light btn-sm',
  buttonLabel = 'Condividi',
  title = 'Condividi la tua fazione',
  iconOnly = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStageLabel, setGenerationStageLabel] = useState('');
  const captureRef = useRef(null);
  const objectUrlRef = useRef('');
  const fileRef = useRef(null);
  const progressAnimationRef = useRef(null);
  const progressValueRef = useRef(0);

  const shareData = useMemo(
    () => buildFactionShareCardData({ faction, members, roster }),
    [faction, members, roster]
  );

  const pageUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return faction?.id
      ? `${window.location.origin}/factions/${faction.id}`
      : window.location.href;
  }, [faction?.id]);

  const shareText = useMemo(
    () => getFactionShareText(faction?.name),
    [faction?.name]
  );

  const captureBackdropImageUrl = ringImg;

  const captureWatermarkImageUrl = useMemo(
    () => (faction?.imageUrl ? getCorsSafeImageUrl(faction.imageUrl) : ''),
    [faction?.imageUrl]
  );

  const fileName = useMemo(
    () => `${sanitizeFileName(faction?.name)}-social-card.png`,
    [faction?.name]
  );

  const captureCacheKey = useMemo(
    () =>
      JSON.stringify({
        factionId: faction?.id || '',
        factionName: faction?.name || '',
        factionImageUrl: faction?.imageUrl || '',
        rosterName: shareData.rosterName || '',
        gameType: shareData.gameType || '',
        members: members.map((member) => ({
          id: member.id,
          name: member.name,
          imageUrl: member.imageUrl,
          status: member.status,
          isInjured: Boolean(member.isInjured),
        })),
        currentLineup: faction?.currentLineup || null,
        lineup: faction?.lineup || null,
      }),
    [
      faction?.currentLineup,
      faction?.id,
      faction?.imageUrl,
      faction?.lineup,
      faction?.name,
      members,
      shareData.gameType,
      shareData.rosterName,
    ]
  );

  const previewSections = useMemo(
    () =>
      shareData.hasLineup
        ? [
            shareData.mainEventer
              ? {
                  id: 'main',
                  label: 'Main Eventer',
                  members: [shareData.mainEventer],
                  variant: 'main',
                }
              : null,
            shareData.inRingMembers.length > 0
              ? {
                  id: 'ring',
                  label: 'In Ring',
                  members: shareData.inRingMembers,
                  variant: 'ring',
                }
              : null,
            shareData.backstageMembers.length > 0
              ? {
                  id: 'backstage',
                  label: 'Backstage',
                  members: shareData.backstageMembers,
                  variant: 'backstage',
                }
              : null,
            shareData.unassignedMembers.length > 0
              ? {
                  id: 'bench',
                  label: 'Non assegnati',
                  members: shareData.unassignedMembers,
                  variant: 'all',
                }
              : null,
          ].filter(Boolean)
        : [
            {
              id: 'all',
              label: 'Roster completo',
              members: shareData.allMembers,
              variant: 'all',
            },
          ],
    [shareData]
  );

  const clearProgressAnimation = useCallback(() => {
    if (progressAnimationRef.current) {
      window.clearInterval(progressAnimationRef.current);
      progressAnimationRef.current = null;
    }
  }, []);

  const setProgressImmediate = useCallback(
    (value) => {
      const nextValue = Math.max(0, Math.min(100, Math.round(value)));
      progressValueRef.current = nextValue;
      setGenerationProgress(nextValue);
    },
    []
  );

  const animateProgressTo = useCallback(
    (targetValue, { duration = 800 } = {}) => {
      const normalizedTarget = Math.max(0, Math.min(100, Math.round(targetValue)));
      const startValue = progressValueRef.current;

      clearProgressAnimation();

      if (normalizedTarget <= startValue || duration <= 0) {
        setProgressImmediate(Math.max(startValue, normalizedTarget));
        return;
      }

      const startedAt = Date.now();

      progressAnimationRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startedAt;
        const completion = Math.min(elapsed / duration, 1);
        const easedCompletion = 1 - Math.pow(1 - completion, 3);
        const nextValue =
          startValue + (normalizedTarget - startValue) * easedCompletion;

        setProgressImmediate(nextValue);

        if (completion >= 1) {
          clearProgressAnimation();
        }
      }, 40);
    },
    [clearProgressAnimation, setProgressImmediate]
  );

  const resetGeneratedAsset = useCallback(() => {
    clearProgressAnimation();
    fileRef.current = null;
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
    }
    setPreviewUrl('');
    setGenerationStageLabel('');
    setProgressImmediate(0);
  }, [clearProgressAnimation, setProgressImmediate]);

  useEffect(() => {
    resetGeneratedAsset();
  }, [captureCacheKey, resetGeneratedAsset]);

  useEffect(
    () => () => {
      clearProgressAnimation();
      resetGeneratedAsset();
    },
    [clearProgressAnimation, resetGeneratedAsset]
  );

  const updatePreviewFromFile = useCallback((file) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }

    objectUrlRef.current = URL.createObjectURL(file);
    setPreviewUrl(objectUrlRef.current);
  }, []);

  const generateShareFile = useCallback(
    async ({ force = false } = {}) => {
      if (fileRef.current && !force) {
        return fileRef.current;
      }

      if (!captureRef.current) {
        throw new Error('Anteprima social non pronta');
      }

      setIsGenerating(true);
      setError('');
      setGenerationStageLabel('Preparo il poster');
      setProgressImmediate(8);

      try {
        await wait(80);
        setGenerationStageLabel('Controllo le immagini wrestler');
        setProgressImmediate(12);
        animateProgressTo(16, { duration: 250 });

        await ensureRequiredMemberImagesReady(previewSections, (progressRatio) => {
          const nextValue = 12 + progressRatio * 32;
          setProgressImmediate(nextValue);
        });

        setGenerationStageLabel('Carico logo e font');
        setProgressImmediate(46);
        animateProgressTo(54, { duration: 400 });

        await waitForCaptureAssets(captureRef.current, (progressRatio) => {
          const nextValue = 46 + progressRatio * 16;
          setProgressImmediate(nextValue);
        });

        setGenerationStageLabel('Disegno la card');
        setProgressImmediate(64);
        animateProgressTo(90, { duration: 1800 });

        const canvas = await html2canvas(captureRef.current, {
          backgroundColor: null,
          useCORS: true,
          logging: false,
          scale: 1.5,
          width: CAPTURE_WIDTH,
          height: CAPTURE_HEIGHT,
          windowWidth: CAPTURE_WIDTH,
          windowHeight: CAPTURE_HEIGHT,
        });

        setGenerationStageLabel('Esporto il PNG');
        setProgressImmediate(92);
        animateProgressTo(96, { duration: 500 });

        const nextFile = await canvasToFile(canvas, fileName);
        fileRef.current = nextFile;
        updatePreviewFromFile(nextFile);
        clearProgressAnimation();
        setGenerationStageLabel('Card pronta');
        setProgressImmediate(100);
        return nextFile;
      } catch (generationError) {
        clearProgressAnimation();
        setGenerationStageLabel('Errore nella generazione');
        throw generationError;
      } finally {
        clearProgressAnimation();
        setIsGenerating(false);
      }
    },
    [
      animateProgressTo,
      clearProgressAnimation,
      fileName,
      previewSections,
      setProgressImmediate,
      updatePreviewFromFile,
    ]
  );

  useEffect(() => {
    if (!isOpen) return;
    void generateShareFile({ force: true }).catch((generationError) => {
      console.error('Errore generazione immagine fazione:', generationError);
      setError(
        generationError?.message || 'Impossibile preparare la card social in questo momento'
      );
    });
  }, [generateShareFile, isOpen, captureCacheKey]);

  const downloadImage = useCallback(
    async ({ silent = false } = {}) => {
      const file = await generateShareFile();
      saveAs(file, file.name);
      if (!silent) {
        setStatusMessage('Immagine scaricata con successo.');
      }
      return file;
    },
    [generateShareFile]
  );

  const shareFileNatively = useCallback(
    async (preferredPlatformLabel = '') => {
      const file = await generateShareFile();

      if (navigator.canShare?.({ files: [file] }) && navigator.share) {
        await navigator.share({
          title: faction?.name || 'Fazione',
          text: preferredPlatformLabel
            ? `Scegli ${preferredPlatformLabel} per condividere la tua card.`
            : shareText,
          files: [file],
        });

        return true;
      }

      return false;
    },
    [faction?.name, generateShareFile, shareText]
  );

  const handleNativeShare = useCallback(async () => {
    setError('');
    setStatusMessage('');

    try {
      const sharedFile = await shareFileNatively();
      if (sharedFile) {
        setStatusMessage('Pannello di condivisione aperto.');
        return;
      }

      await downloadImage({ silent: true });

      if (navigator.share) {
        await navigator.share({
          title: faction?.name || 'Fazione',
          text: `${shareText} ${pageUrl}`.trim(),
          url: pageUrl,
        });
        setStatusMessage(
          'Immagine scaricata. Sul tuo browser abbiamo condiviso il link della fazione.'
        );
        return;
      }

      setStatusMessage(
        'Immagine scaricata. Ora puoi ricondividerla nelle tue app social preferite.'
      );
    } catch (shareError) {
      if (shareError?.name === 'AbortError') {
        return;
      }

      console.error('Errore condivisione nativa fazione:', shareError);
      setError(
        shareError?.message || 'Impossibile aprire la condivisione nativa in questo momento'
      );
    }
  }, [downloadImage, faction?.name, pageUrl, shareFileNatively, shareText]);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setStatusMessage('Link della fazione copiato negli appunti.');
      setError('');
    } catch (copyError) {
      console.error('Errore copia link fazione:', copyError);
      setError('Impossibile copiare il link della fazione');
    }
  }, [pageUrl]);

  const openPlatformUrl = useCallback(
    (platform) => {
      if (!platform?.buildUrl || typeof window === 'undefined') return;

      const targetUrl = platform.buildUrl({
        text: shareText,
        url: pageUrl,
      });

      if (targetUrl) {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      }
    },
    [pageUrl, shareText]
  );

  const handlePlatformShare = useCallback(
    async (platform) => {
      setError('');
      setStatusMessage('');

      try {
        const sharedFile = await shareFileNatively(platform.label);
        if (sharedFile) {
          setStatusMessage(
            `Pannello di condivisione aperto. Seleziona ${platform.label} per inviare la card.`
          );
          return;
        }

        await downloadImage({ silent: true });
        openPlatformUrl(platform);
        setStatusMessage(platform.fallbackMessage);
      } catch (platformError) {
        if (platformError?.name === 'AbortError') {
          return;
        }

        console.error(`Errore condivisione ${platform.label}:`, platformError);
        setError(
          platformError?.message || `Impossibile preparare la condivisione per ${platform.label}`
        );
      }
    },
    [downloadImage, openPlatformUrl, shareFileNatively]
  );

  const progressRingOffset =
    PROGRESS_RING_CIRCUMFERENCE * (1 - generationProgress / 100);
  const generationStatusLabel =
    generationStageLabel || 'Sto preparando la card social...';

  return (
    <>
      {/*<button
        type="button"
        className={buttonClassName}
        title={title}
        aria-label={title}
        onClick={() => {
          setError('');
          setStatusMessage('');
          setIsOpen(true);
        }}
        disabled={members.length === 0}
      >
        <i className={`bi bi-share-fill${iconOnly ? '' : ' me-2'}`}></i>
        {!iconOnly && buttonLabel}
      </button>
      */}

      {isOpen && (
        <>
          <Modal
            title="Condividi la tua fazione"
            icon="bi-share-fill"
            onClose={() => setIsOpen(false)}
          >
            <div className="faction-share-modal">
              <div className="faction-share-modal__intro">
                Poster 9:16 pensato per storie e condivisioni social. Se c&apos;e una line-up
                mostra Main Eventer, In Ring e Backstage; altrimenti ordina il roster per nome.
              </div>

              {error && (
                <div className="alert alert-danger py-2 mb-3">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                </div>
              )}

              {statusMessage && (
                <div className="alert alert-info py-2 mb-3">
                  <i className="bi bi-info-circle-fill me-2"></i>
                  {statusMessage}
                </div>
              )}

              <div className="faction-share-modal__preview-shell">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt={`Anteprima social di ${faction?.name || 'questa fazione'}`}
                    className="faction-share-modal__preview-image"
                  />
                ) : (
                  <div className="faction-share-modal__preview-loading">
                    <div
                      className="faction-share-progress"
                      role="status"
                      aria-live="polite"
                      aria-label={`Generazione immagine al ${generationProgress}%`}
                    >
                      <svg
                        viewBox="0 0 120 120"
                        className="faction-share-progress__svg"
                        aria-hidden="true"
                      >
                        <circle
                          className="faction-share-progress__track"
                          cx="60"
                          cy="60"
                          r={PROGRESS_RING_RADIUS}
                        />
                        <circle
                          className="faction-share-progress__value"
                          cx="60"
                          cy="60"
                          r={PROGRESS_RING_RADIUS}
                          style={{
                            strokeDasharray: PROGRESS_RING_CIRCUMFERENCE,
                            strokeDashoffset: progressRingOffset,
                          }}
                        />
                      </svg>
                      <div className="faction-share-progress__value-label">
                        {generationProgress}%
                      </div>
                    </div>
                    <strong className="faction-share-progress__title">
                      {generationStatusLabel}
                    </strong>
                    <span className="faction-share-progress__hint">
                      Il cerchio si riempie man mano che il poster viene renderizzato.
                    </span>
                  </div>
                )}

                {isGenerating && previewUrl && (
                  <div className="faction-share-modal__preview-badge">
                    <strong>{generationProgress}%</strong>
                    <span>{generationStatusLabel}</span>
                  </div>
                )}
              </div>

              <div className="faction-share-modal__actions">
                <button
                  type="button"
                  className="btn btn-brand-secondary"
                  onClick={handleNativeShare}
                  disabled={isGenerating}
                >
                  <i className="bi bi-phone me-2"></i>
                  Condividi immagine
                </button>
                <button
                  type="button"
                  className="btn btn-outline-light"
                  onClick={() => {
                    void downloadImage();
                  }}
                  disabled={isGenerating}
                >
                  <i className="bi bi-download me-2"></i>
                  Scarica PNG
                </button>
                <button
                  type="button"
                  className="btn btn-outline-light"
                  onClick={handleCopyLink}
                >
                  <i className="bi bi-link-45deg me-2"></i>
                  Copia link
                </button>
              </div>

              <div className="faction-share-modal__platforms">
                {SOCIAL_PLATFORMS.map((platform) => (
                  <button
                    key={platform.id}
                    type="button"
                    className={`faction-share-platform-btn faction-share-platform-btn--${platform.id}`}
                    onClick={() => {
                      void handlePlatformShare(platform);
                    }}
                    disabled={isGenerating}
                  >
                    <i className={`bi ${platform.icon}`}></i>
                    <span>{platform.label}</span>
                  </button>
                ))}
              </div>

              <p className="faction-share-modal__note mb-0">
                Su mobile provo a passare direttamente il file immagine al foglio di condivisione,
                cosi puo finire su WhatsApp, Telegram, Instagram o Facebook. Se una foto wrestler
                non risponde la ricarico automaticamente; se continua a mancare ti mostro
                l&apos;errore.
              </p>
            </div>
          </Modal>

          <div className="faction-share-capture-host" aria-hidden="true">
            <div
              ref={captureRef}
              className="faction-share-capture"
              style={{ width: CAPTURE_WIDTH, height: CAPTURE_HEIGHT }}
            >
              <img
                src={captureBackdropImageUrl}
                alt=""
                aria-hidden="true"
                crossOrigin="anonymous"
                className="faction-share-capture__backdrop"
                onError={(event) => {
                  if (event.currentTarget.dataset.fallbackApplied === 'true') return;
                  event.currentTarget.dataset.fallbackApplied = 'true';
                  event.currentTarget.src = ringImg;
                }}
              />
              <div className="faction-share-capture__backdrop-overlay" />
              <div className="faction-share-capture__backdrop-glow" />

              {captureWatermarkImageUrl && (
                <img
                  src={captureWatermarkImageUrl}
                  alt=""
                  aria-hidden="true"
                  crossOrigin="anonymous"
                  className="faction-share-capture__watermark"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              )}

              <div className="faction-share-capture__content">
                <header className="faction-share-capture__header">
                  <h1 className="faction-share-capture__title">{shareData.title}</h1>
                  {captureWatermarkImageUrl && (
                    <div className="faction-share-capture__brand-mark">
                      <img
                        src={captureWatermarkImageUrl}
                        alt=""
                        aria-hidden="true"
                        crossOrigin="anonymous"
                        className="faction-share-capture__brand-mark-image"
                        onError={event => {
                          event.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  <div className="faction-share-capture__meta">
                    {shareData.gameType && (
                      <span className="faction-share-capture__meta-pill">
                        {shareData.gameType}
                      </span>
                    )}
                    {shareData.rosterName && (
                      <span className="faction-share-capture__meta-pill">
                        {shareData.rosterName}
                      </span>
                    )}
                  </div>
                </header>

                <div className="faction-share-capture__body">
                  {previewSections.map((section) => (
                    <section
                      key={section.id}
                      className={[
                        'faction-share-section',
                        `faction-share-section--${section.variant}`,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <div className="faction-share-section__header">
                        <span>{section.label}</span>
                      </div>

                      {section.variant === 'main' ? (
                        <div
                          className="faction-share-member-grid faction-share-member-grid--main"
                          style={getGridMetrics(section.members.length, 'main')}
                        >
                          <ShareMemberCard member={section.members[0]} variant="main" />
                        </div>
                      ) : (
                        <div
                          className={[
                            'faction-share-member-grid',
                            section.variant === 'backstage'
                              ? 'faction-share-member-grid--backstage'
                              : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          style={getGridMetrics(section.members.length, section.variant)}
                        >
                          {section.members.map((member) => (
                            <ShareMemberCard
                              key={`${section.id}-${member.id}`}
                              member={member}
                              variant={section.variant === 'backstage' ? 'backstage' : 'ring'}
                            />
                          ))}
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
